import type { ApprovalAttributes } from '@/types/approval';
import {
  SERP_NOT_LOADED_MESSAGE,
  type SerpDocumentRef,
} from '@/utils/approval/serpDocument';

/** SERP 매장 개점 — 레거시 appr_edit_forms_serp_shop_open */
export const SERP_STORE_OPEN_TYPE = 161;

export const isSerpStoreOpenType = (apvType: number): boolean =>
  apvType === SERP_STORE_OPEN_TYPE;

/** 라벨·값 칸 한 쌍. null 은 빈 칸 */
export type SerpStoreOpenCell = { key: string; label: string } | null;

/** 표별 행 구성 — 레거시 칸 배치(빈 칸 포함) 그대로 */
export const SERP_STORE_OPEN_SECTIONS: {
  title: string;
  rows: SerpStoreOpenCell[][];
}[] = [
  {
    title: '매장 기본 정보',
    rows: [
      [
        { key: 'BRAND_NM', label: '브랜드' },
        { key: 'SHOP_CD', label: '매장코드' },
        { key: 'SHOP_NM', label: '매장명(정식)' },
      ],
      [
        { key: 'BRN', label: '사업자번호' },
        { key: 'TRN', label: '(종번호)' },
        { key: 'SHOP_ABBR_NM', label: '매장명(약칭)' },
      ],
      [
        { key: 'BT_NM', label: '업태' },
        { key: 'BI_NM', label: '종목' },
        { key: 'REP_NM', label: '대표자' },
      ],
      [
        { key: 'BRN_STA_DE', label: '사업자번호 변경일자' },
        { key: 'LOAN_STA_DE', label: '매장여신 시작일자' },
        { key: 'RRN', label: '법인번호/주민등록번호' },
      ],
      [
        { key: 'ZIP', label: '우편번호' },
        { key: 'BSC_ADDR', label: '기본주소' },
        { key: 'DTL_ADDR', label: '상세주소' },
      ],
      [
        { key: 'TEL_NO', label: '전화번호(유선)' },
        { key: 'CEL_NO', label: '전화번호(핸드폰)' },
        null,
      ],
    ],
  },
  {
    title: '관리 정보',
    rows: [
      [
        { key: 'SHOP_TRD_TYP', label: '매장거래유형' },
        { key: 'SHOP_MGMT_TYP', label: '매장관리유형' },
        { key: 'PROD_TRD_TYP', label: '상품거래유형' },
      ],
      [
        { key: 'SHOP_CTRT_TYP', label: '매장계약유형' },
        { key: 'SHOP_SALE_DIV', label: '판매구분' },
        null,
      ],
      [{ key: 'DN_DIV', label: '유통망구분' }, null, null],
      [
        { key: 'GI_PSBT_YN', label: '출고가능여부' },
        { key: 'SALE_PSBT_YN', label: '판매가능여부' },
        null,
      ],
    ],
  },
  {
    title: '분석 정보',
    rows: [[{ key: 'RF_DIV', label: 'RF구분' }, null, null]],
  },
  {
    title: 'I/F 정보',
    rows: [
      [
        { key: 'SAP_CH', label: 'SAP 채널구분' },
        { key: 'SAP_MOV_TYP', label: 'SAP 이동유형' },
      ],
    ],
  },
  {
    title: '회계 정보',
    rows: [[{ key: 'CTB_TYP', label: '수금유형' }]],
  },
];

/** 수금계좌정보 열 — 순번 다음부터 */
export const SERP_STORE_OPEN_ACCOUNT_COLUMNS = [
  { key: 'CTB_FORM', label: '지급/수금형태' },
  { key: 'PMT_TYP', label: '지급유형' },
  { key: 'BANK_CTRY_CD', label: '은행국가' },
  { key: 'BANK_NM', label: '은행코드' },
  { key: 'BANK_AC_NO', label: '계좌번호' },
  { key: 'DEPR_NM', label: '예금주' },
  { key: 'USE_YN', label: '사용유무' },
] as const;

export type SerpStoreOpenAccount = Record<string, unknown>;

/** EAI 응답 원문 — 저장 attributes.data 에 손대지 않고 그대로 보관 */
export type SerpStoreOpenSource = {
  APRV_TITLE?: string | null;
  DTL_LIST?: SerpStoreOpenAccount[] | null;
  [key: string]: unknown;
};

export type SerpStoreOpenDraftSource = {
  code: number;
  attributes: SerpDocumentRef;
  display: { source?: SerpStoreOpenSource | null };
};

export type SerpStoreOpenState = SerpDocumentRef & {
  source: SerpStoreOpenSource;
  /** SERP 응답으로 자동 입력한 제목 — 이탈 경고는 이 값과 비교. 편집은 미사용 */
  autoSubject: string;
};

/** 레거시 `$('#subject').val(APRV_TITLE)` — 키가 없으면(undefined) 제목을 건드리지 않고, null 이면 비움 */
export const buildSerpStoreOpenSubject = (
  source: SerpStoreOpenSource,
): string | undefined => {
  if (source.APRV_TITLE === undefined) return undefined;
  return source.APRV_TITLE ?? '';
};

/** SERP 응답에 DTL_LIST 가 null 로 오는 문서가 많음 */
export const getSerpStoreOpenAccounts = (
  source: SerpStoreOpenSource,
): SerpStoreOpenAccount[] =>
  Array.isArray(source.DTL_LIST)
    ? source.DTL_LIST.filter(
        (row): row is SerpStoreOpenAccount => !!row && typeof row === 'object',
      )
    : [];

export const checkSerpStoreOpenBeforeSave = (
  state: SerpStoreOpenState | null | undefined,
): string | null => (state?.source ? null : SERP_NOT_LOADED_MESSAGE);

export const buildSerpStoreOpenAttributes = (
  state: SerpStoreOpenState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.source
    ? {
        typeCode: state.typeCode,
        sequence: state.sequence,
        content,
        data: state.source,
      }
    : null;
